import { useEffect, useState, useCallback } from "react";
import { Navigate, useParams, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Archive, ArchiveRestore } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import AdminShell from "@/components/admin/AdminShell";
import { ORG_TYPE_LABELS, CASE_STATUS_LABELS, formatDate } from "@/constants/business_status";

export default function OrganizationDetailPage() {
  const { admin } = useAdminAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get(`/admin/organizations/${id}`);
      setData(r.data);
    } catch { toast.error("Organisation introuvable"); navigate("/admin/organisations"); }
    finally { setLoading(false); }
  }, [id, navigate]);
  useEffect(() => { if (admin) load(); }, [admin, load]);

  if (admin === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>;
  if (admin === false) return <Navigate to="/admin/login" replace />;
  if (loading || !data) return <AdminShell><div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-navy" /></div></AdminShell>;

  const { organization: o, contacts, cases } = data;
  const doArchive = async () => { if (!window.confirm("Archiver cette organisation ?")) return; await apiClient.post(`/admin/organizations/${id}/archive`); toast.success("Archivé"); load(); };
  const doRestore = async () => { await apiClient.post(`/admin/organizations/${id}/restore`); toast.success("Restauré"); load(); };

  return (
    <AdminShell>
      <div className="mb-5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Button data-testid="org-back" variant="ghost" onClick={() => navigate("/admin/organisations")} className="text-navy font-semibold -ml-3"><ArrowLeft className="w-4 h-4 mr-1" />Retour</Button>
          <h1 data-testid="org-detail-header" className="font-heading font-extrabold text-2xl text-navy">{o.legal_name}</h1>
          <p className="text-xs text-muted-foreground mt-1">
            {ORG_TYPE_LABELS[o.organization_type] || o.organization_type} • Créée le {formatDate(o.created_at)}
            {o.archived_at ? " • Archivée" : ""}
          </p>
        </div>
        <div className="flex gap-2">
          {o.archived_at
            ? <Button data-testid="org-restore" onClick={doRestore} variant="outline" className="rounded-full"><ArchiveRestore className="w-4 h-4 mr-1" /> Restaurer</Button>
            : <Button data-testid="org-archive" onClick={doArchive} variant="outline" className="rounded-full text-orange border-orange/40 hover:bg-orange/5"><Archive className="w-4 h-4 mr-1" /> Archiver</Button>}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-gray-100 rounded-2xl bg-white p-5" data-testid="org-section-coords">
          <h3 className="font-heading font-semibold text-navy text-sm mb-3">Coordonnées</h3>
          <div className="space-y-2 text-sm">
            <p><span className="font-medium text-navy/70 text-xs">Email :</span> {o.email || "—"}</p>
            <p><span className="font-medium text-navy/70 text-xs">Téléphone :</span> {o.phone || "—"}</p>
            <p><span className="font-medium text-navy/70 text-xs">Nom commercial :</span> {o.trade_name || "—"}</p>
            {o.notes && <p className="text-muted-foreground pt-2 border-t border-gray-100">{o.notes}</p>}
          </div>
        </div>

        <div className="border border-gray-100 rounded-2xl bg-white p-5" data-testid="org-section-contacts">
          <h3 className="font-heading font-semibold text-navy text-sm mb-3">Contacts liés ({contacts.length})</h3>
          {contacts.length === 0 ? <p className="text-muted-foreground text-sm">Aucun</p> : (
            <ul className="space-y-1 text-sm">
              {contacts.map((c) => (
                <li key={c.id}>
                  <button onClick={() => navigate(`/admin/contacts/${c.id}`)} className="text-navy hover:text-orange font-medium">
                    {c.display_name} <span className="text-xs text-muted-foreground">{c.phone}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border border-gray-100 rounded-2xl bg-white p-5 md:col-span-2" data-testid="org-section-cases">
          <h3 className="font-heading font-semibold text-navy text-sm mb-3">Dossiers ({cases.length})</h3>
          {cases.length === 0 ? <p className="text-muted-foreground text-sm">Aucun dossier</p> : (
            <div className="divide-y divide-gray-100">
              {cases.map((c) => (
                <button key={c.id} onClick={() => navigate(`/admin/dossiers/${c.id}`)} className="w-full text-left py-2 hover:bg-graylight rounded px-2 flex justify-between items-center">
                  <span>
                    <span className="font-semibold text-navy text-sm">{c.case_number}</span>
                    <span className="text-xs text-muted-foreground ml-2">{c.service_type} • {formatDate(c.created_at)}</span>
                  </span>
                  <span className="text-xs font-semibold bg-navy/5 text-navy rounded-full px-2 py-0.5">{CASE_STATUS_LABELS[c.status] || c.status}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
